<template>
  <div class="linha-assistente-kml-map-panel d-flex flex-column fill-height">
    <v-toolbar flat color="primary" dark class="flex-shrink-0 linha-assistente-kml-map-toolbar">
      <v-toolbar-title class="text-truncate pr-2">{{ title }}</v-toolbar-title>
      <v-spacer />
      <v-btn icon dark @click="forwardInput(false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <template v-slot:extension>
        <div class="d-flex align-center" style="width: 100%;" aria-hidden="true" />
      </template>
    </v-toolbar>
    <v-card-text v-if="showRouteHighlightSelect" class="flex-shrink-0 px-3 pt-2 pb-2 kml-fence-map-select-wrap">
      <v-select
        :value="routeHighlightIndex"
        :items="routeHighlightItems"
        item-text="text"
        item-value="value"
        item-disabled="disabled"
        dense
        hide-details
        outlined
        :menu-props="{ offsetY: true, maxHeight: 320, attach: true }"
        :label="$t('crudLinhas.kmlAssistant.highlightRouteOnMap')"
        @input="$emit('update:routeHighlightIndex', $event)"
      />
    </v-card-text>
    <div class="kml-assistant-map-wrap flex-grow-1">
      <l-map
        ref="kmlGeometryMap"
        :key="'kgeom-' + mapKey"
        :zoom="preview.zoom"
        :center="preview.center"
        :options="{ zoomSnap: 0.5, preferCanvas: true }"
        class="kml-assistant-map-el"
        @ready="onMapReady"
      >
        <l-tile-layer :url="tileUrl" :attribution="tileAttribution" />
        <l-geo-json
          v-if="preview.secondaryRoutesGeojson"
          :geojson="preview.secondaryRoutesGeojson"
          :options="routeSecondaryRoutesClickOptions"
          :options-style="preview.secondaryRoutesStyle"
        />
        <l-geo-json
          v-if="preview.fenceMapHighlightedRouteFeature"
          :geojson="preview.fenceMapHighlightedRouteFeature"
          :options="routeFenceHighlightClickOptions"
          :options-style="preview.fenceMapHighlightedRouteStyle"
        />
        <l-geo-json
          v-if="scope === 'route' && preview.geojsonFeature"
          :geojson="preview.geojsonFeature"
          :options="routePrimaryPreviewClickOptions"
          :options-style="preview.geojsonStyle"
        />
        <l-geo-json
          v-for="layer in fenceLayersWithGeojson"
          :key="'fgj-' + layer.id"
          :geojson="layer.geojsonFeature"
          :options="fenceStopGeoJsonClickOptions"
          :options-style="layer.geojsonStyle"
        />
        <l-circle
          v-for="layer in fenceLayersWithCircle"
          :key="'fc-' + layer.id"
          ref="fenceCircles"
          :lat-lng="layer.circle.latLng"
          :radius="layer.circle.radius"
          :color="layer.circle.color"
          :fill-color="layer.circle.fillColor"
          :fill-opacity="layer.circle.fillOpacity"
          :weight="layer.circle.weight"
          :options="fenceShapePointerOptions"
          @ready="onFenceShapeReady(layer, $event)"
          @click="emitSelectFence(layer.id)"
        />
        <l-marker
          v-for="layer in fenceLayersWithMarker"
          :key="'fm-' + layer.id"
          ref="fenceMarkers"
          :lat-lng="layer.markerLatLng"
          :options="fenceShapePointerOptions"
          @ready="onFenceShapeReady(layer, $event)"
          @click="emitSelectFence(layer.id)"
        />
      </l-map>
    </div>
  </div>
</template>

<script>
import L from 'leaflet';
import { LMap, LTileLayer, LGeoJson, LCircle, LMarker } from 'vue2-leaflet';

export default {
  name: 'LinhaAssistenteKmlMapOverlay',
  components: {
    LMap,
    LTileLayer,
    LGeoJson,
    LCircle,
    LMarker,
  },
  props: {
    title: { type: String, default: '' },
    scope: { type: String, default: 'route' },
    mapKey: { type: Number, default: 0 },
    preview: {
      type: Object,
      required: true,
    },
    fenceLayers: {
      type: Array,
      default: () => [],
    },
    tileUrl: { type: String, required: true },
    tileAttribution: { type: String, required: true },
    showRouteHighlightSelect: { type: Boolean, default: false },
    routeHighlightItems: { type: Array, default: () => [] },
    routeHighlightIndex: { type: [Number, String], default: null },
    /** KML route row index for the bold line in route map preview. */
    routePreviewFocusIndex: { default: null },
    /** KML route row index for the bold line in fence/stops map preview. */
    highlightedRouteRowIndex: { default: null },
  },
  data() {
    return {
      fenceStopGeoJsonClickOptions: {},
      routeSecondaryRoutesClickOptions: {},
      routePrimaryPreviewClickOptions: {},
      routeFenceHighlightClickOptions: {},
    };
  },
  created() {
    this.routeSecondaryRoutesClickOptions = {
      onEachFeature: (feature, leafletLayer) => {
        leafletLayer.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          const raw = feature && feature.properties && feature.properties.kmlRouteRowIndex;
          if (raw === undefined || raw === null || raw === '') return;
          const i = Number(raw);
          if (!Number.isFinite(i)) return;
          this.emitSelectRoute(i);
        });
      },
    };
    this.routePrimaryPreviewClickOptions = {
      onEachFeature: (feature, leafletLayer) => {
        leafletLayer.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          const idx = this.routePreviewFocusIndex;
          if (idx === undefined || idx === null) return;
          this.emitSelectRoute(idx);
        });
      },
    };
    this.routeFenceHighlightClickOptions = {
      onEachFeature: (feature, leafletLayer) => {
        leafletLayer.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          const idx = this.highlightedRouteRowIndex;
          if (idx === undefined || idx === null) return;
          this.emitSelectRoute(idx);
        });
      },
    };
    this.fenceStopGeoJsonClickOptions = {
      onEachFeature: (feature, leafletLayer) => {
        const tip = feature && feature.properties && feature.properties.kmlFenceTooltip;
        this.bindFenceTooltipOnLayer(leafletLayer, tip);
        leafletLayer.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          const raw = feature && feature.properties && feature.properties.kmlFenceRowIndex;
          if (raw === undefined || raw === null || raw === '') return;
          const i = Number(raw);
          if (!Number.isFinite(i)) return;
          this.emitSelectFence(i);
        });
      },
    };
  },
  computed: {
    fenceShapePointerOptions() {
      return { className: 'kml-map-stop-layer' };
    },
    fenceLayersWithGeojson() {
      return (this.fenceLayers || []).filter((l) => l.geojsonFeature);
    },
    fenceLayersWithCircle() {
      return (this.fenceLayers || []).filter((l) => l.circle);
    },
    fenceLayersWithMarker() {
      return (this.fenceLayers || []).filter((l) => l.markerLatLng);
    },
  },
  watch: {
    mapKey() {
      this.scheduleFitBounds();
    },
    preview: {
      deep: true,
      handler() {
        this.scheduleFitBounds();
      },
    },
    fenceLayers: {
      deep: true,
      handler() {
        this.scheduleFitBounds();
        this.syncFenceShapeTooltips();
      },
    },
  },
  mounted() {
    this._onKmlMapResize = () => this.scheduleFitBounds();
    window.addEventListener('resize', this._onKmlMapResize);
    this.$nextTick(() => this.scheduleFitBounds());
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onKmlMapResize);
  },
  methods: {
    forwardInput(v) {
      this.$emit('input', v);
    },
    fenceStopTooltipOptions() {
      return {
        sticky: true,
        direction: 'auto',
        className: 'kml-map-stop-tooltip',
        opacity: 0.95,
      };
    },
    bindFenceTooltipOnLayer(leafletLayer, tooltipText) {
      if (!leafletLayer) return;
      if (tooltipText) {
        leafletLayer.bindTooltip(tooltipText, this.fenceStopTooltipOptions());
      } else {
        leafletLayer.unbindTooltip();
      }
    },
    onFenceShapeReady(layer, mapObject) {
      if (!mapObject || !layer) return;
      this.bindFenceTooltipOnLayer(mapObject, layer.tooltipText);
    },
    syncFenceShapeTooltips() {
      this.$nextTick(() => {
        this.applyTooltipToFenceShapeRefs(this.$refs.fenceCircles, this.fenceLayersWithCircle);
        this.applyTooltipToFenceShapeRefs(this.$refs.fenceMarkers, this.fenceLayersWithMarker);
      });
    },
    applyTooltipToFenceShapeRefs(refs, layers) {
      if (!layers || !layers.length) return;
      const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
      layers.forEach((layer, i) => {
        const comp = list[i];
        const mo = comp && comp.mapObject;
        if (!mo) return;
        this.bindFenceTooltipOnLayer(mo, layer.tooltipText);
      });
    },
    emitSelectFence(fenceIndex) {
      this.$emit('select-fence', fenceIndex);
    },
    emitSelectRoute(routeRowIndex) {
      this.$emit('select-route', routeRowIndex);
    },
    onMapReady() {
      this.scheduleFitBounds();
    },
    scheduleFitBounds() {
      this.$nextTick(() => {
        this.fitBounds();
        requestAnimationFrame(() => this.fitBounds());
      });
      setTimeout(() => this.fitBounds(), 280);
    },
    extendFenceLayerBounds(extend, layer) {
      if (layer.geojsonFeature) {
        extend(L.geoJSON(layer.geojsonFeature).getBounds());
      }
      if (layer.circle) {
        const { latLng, radius } = layer.circle;
        extend(L.circle(latLng, { radius }).getBounds());
      }
      if (layer.markerLatLng) {
        const p = layer.markerLatLng;
        extend(L.latLngBounds(p, p));
      }
    },
    fitBounds() {
      const ref = this.$refs.kmlGeometryMap;
      const map = ref && ref.mapObject;
      if (!map) return;
      try {
        let combined = null;
        const extend = (bounds) => {
          if (!bounds || !bounds.isValid()) return;
          combined = combined ? combined.extend(bounds) : L.latLngBounds(bounds);
        };
        if (this.preview.secondaryRoutesGeojson) {
          extend(L.geoJSON(this.preview.secondaryRoutesGeojson).getBounds());
        }
        if (this.preview.fenceMapHighlightedRouteFeature) {
          extend(L.geoJSON(this.preview.fenceMapHighlightedRouteFeature).getBounds());
        }
        if (this.scope === 'route' && this.preview.geojsonFeature) {
          extend(L.geoJSON(this.preview.geojsonFeature).getBounds());
        }
        (this.fenceLayers || []).forEach((layer) => this.extendFenceLayerBounds(extend, layer));
        if (combined && combined.isValid()) {
          const sw = combined.getSouthWest();
          const ne = combined.getNorthEast();
          if (sw.equals(ne)) {
            map.setView(sw, 16);
          } else {
            map.fitBounds(combined.pad(0.08), { padding: [40, 40], maxZoom: 17 });
          }
        } else {
          const firstMarker = (this.fenceLayers || []).find((l) => l.markerLatLng);
          if (firstMarker && firstMarker.markerLatLng) {
            map.setView(firstMarker.markerLatLng, 16);
          }
        }
        map.invalidateSize();
      } catch (e) {
        console.warn(e);
      }
    },
  },
};
</script>

<style scoped>
.linha-assistente-kml-map-panel {
  min-height: 0;
  width: 100%;
  height: 100%;
}
.kml-assistant-map-wrap {
  position: relative;
  width: 100%;
  min-height: 0;
}
.kml-assistant-map-el {
  height: 100%;
  min-height: 200px;
  width: 100%;
  z-index: 1;
}
.kml-fence-map-select-wrap {
  position: relative;
  z-index: 4;
}
/* Same structure as LinhaAssistenteDialog wizard toolbar: 64px row + 48px extension (Vuetify defaults). */
.linha-assistente-kml-map-toolbar {
  flex: 0 0 auto !important;
  height: 55px !important;
}
</style>

<style>
.kml-map-stop-layer,
.kml-map-route-layer {
  cursor: pointer !important;
}
.leaflet-tooltip.kml-map-stop-tooltip {
  max-width: 280px;
  white-space: pre-line;
  line-height: 1.35;
}
</style>
